import { useState } from 'react';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { Link2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../branding/BrandingContext';
import SettingsMenuButton from './common/SettingsMenuButton';
import { useTheme } from '@mui/material/styles';
import { useAuthSession } from '../contexts/AuthSessionContext';
import ProductCheckoutModal from './billing/ProductCheckoutModal';
import { PRODUCT_CATALOG } from '../utils/productCatalog';

const produtos = PRODUCT_CATALOG;

const Products = () => {
  const branding = useBranding();
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    session,
    user,
    isAdmin,
    isOwner,
    hasProductAccess,
    getProductEntryPath,
  } = useAuthSession();
  const suiteName = branding.copy?.suiteName || branding.name || 'Gourmex';
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const columns = isMdUp ? 3 : isSmUp ? 2 : 1;
  const [checkoutProductKey, setCheckoutProductKey] = useState(null);

  const canAccessProductCard = (produto) => {
    if (!session) return false;
    if (produto.adminOnly && !isAdmin) return false;
    return hasProductAccess(produto.productKey, { allowTrialRestricted: true });
  };

  const getProductPath = (produto) =>
    getProductEntryPath(produto.productKey, produto.link);

  const cardAnimation = {
    '@keyframes cardReveal': {
      '0%': { opacity: 0, transform: 'translateY(14px) scale(0.98)' },
      '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
    },
  };

  const productPriority = (produto) => {
    if (produto.comingSoon) return 0;
    if (canAccessProductCard(produto)) return 2;
    return 1;
  };
  const sortedProdutos = [...produtos].sort(
    (a, b) => productPriority(b) - productPriority(a)
  );
  const visibleProdutos = sortedProdutos.filter(
    (produto) => (
      !produto.comingSoon
      && (isOwner || canAccessProductCard(produto))
    )
  );
  const remainder = visibleProdutos.length % columns;
  const placeholdersCount = columns > 1 && remainder !== 0 ? columns - remainder : 0;

  const accessibleCount = produtos.filter(
    (p) => canAccessProductCard(p) && !p.comingSoon
  ).length;
  const availableCount = isOwner
    ? produtos.filter(
      (p) => !canAccessProductCard(p) && !p.comingSoon && !p.activationDisabled
    ).length
    : 0;
  const greetingHour = new Date().getHours();
  const greeting =
    greetingHour < 12 ? 'Bom dia' : greetingHour < 18 ? 'Boa tarde' : 'Boa noite';

  const myLinksButtonSx = {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.86rem',
    letterSpacing: '0.01em',
    color: '#eaf3ff',
    borderRadius: '999px',
    px: 2,
    py: 0.9,
    gap: 0.5,
    border: '1px solid rgba(124,243,255,0.30)',
    background:
      'linear-gradient(135deg, rgba(124,243,255,0.16) 0%, rgba(123,123,255,0.10) 60%, rgba(255,216,107,0.06) 100%)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    boxShadow:
      '0 14px 34px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.10)',
    transition:
      'transform 0.25s ease, background 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
    '& svg': { color: '#7cf3ff' },
    '&:hover': {
      background:
        'linear-gradient(135deg, rgba(124,243,255,0.24) 0%, rgba(183,148,255,0.16) 60%, rgba(255,216,107,0.10) 100%)',
      borderColor: 'rgba(124,243,255,0.5)',
      boxShadow:
        '0 18px 44px rgba(124,243,255,0.20), inset 0 1px 0 rgba(255,255,255,0.16)',
      transform: 'translateY(-1px)',
    },
  };

  const renderMyLinksButton = (wrapperSx) => (
    <Button
      onClick={() => navigate('/products/my-links')}
      startIcon={<Link2 size={16} />}
      sx={{ ...myLinksButtonSx, ...wrapperSx }}
    >
      Meus Links
    </Button>
  );

  return (
    <>
      <SettingsMenuButton
        buttonProps={{
          sx: {
            position: 'fixed',
            top: { xs: 16, md: 22 },
            right: { xs: 16, md: 22 },
            zIndex: 20,
            width: 46,
            height: 46,
            color: '#f5f7ff',
            borderRadius: '14px',
            background:
              'linear-gradient(135deg, rgba(124,243,255,0.12) 0%, rgba(123,123,255,0.07) 60%, rgba(255,216,107,0.05) 100%)',
            border: '1px solid rgba(255,255,255,0.14)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            boxShadow:
              '0 14px 34px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.10)',
            transition:
              'transform 0.3s ease, background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
            '& svg': {
              transition: 'transform 0.7s cubic-bezier(0.4, 0.0, 0.2, 1)',
              filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.35))',
            },
            '&:hover': {
              background:
                'linear-gradient(135deg, rgba(124,243,255,0.22) 0%, rgba(183,148,255,0.16) 60%, rgba(255,216,107,0.10) 100%)',
              borderColor: 'rgba(124,243,255,0.36)',
              boxShadow:
                '0 18px 46px rgba(124,243,255,0.22), 0 0 0 6px rgba(124,243,255,0.06), inset 0 1px 0 rgba(255,255,255,0.16)',
              transform: 'translateY(-1px)',
            },
            '&:hover svg': {
              transform: 'rotate(75deg)',
              color: '#7cf3ff',
            },
            '&:active': {
              transform: 'translateY(0) scale(0.96)',
            },
            '&:focus-visible': {
              outline: 'none',
              boxShadow:
                '0 0 0 3px rgba(124,243,255,0.45), 0 14px 34px rgba(0,0,0,0.4)',
            },
          },
        }}
      />

      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          boxSizing: 'border-box',
          px: { xs: 2.5, md: 6 },
          py: { xs: 5, md: 'clamp(44px, 8vh, 72px)' },
          background: `
            radial-gradient(circle at 10% 20%, rgba(124, 243, 255, 0.14), transparent 24%),
            radial-gradient(circle at 80% 0%, rgba(123, 123, 255, 0.1), transparent 25%),
            radial-gradient(circle at 80% 80%, rgba(255, 219, 47, 0.08), transparent 24%),
            linear-gradient(130deg, #070c1a 0%, #0c1324 40%, #0b0f1d 100%)
          `,
          color: '#e9ecf5',
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(180px at 30% 50%, rgba(255,255,255,0.06), transparent 50%)',
            filter: 'blur(12px)',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            maxWidth: '1200px',
            width: '100%',
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 3, md: 'clamp(28px, 5vh, 40px)' },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 2, md: 2.25 },
              textAlign: { xs: 'left', md: 'center' },
              alignItems: { xs: 'flex-start', md: 'center' },
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                px: 2.5,
                py: 1,
                borderRadius: '999px',
                border: '1px solid rgba(124, 243, 255, 0.22)',
                background:
                  'linear-gradient(135deg, rgba(124,243,255,0.10), rgba(123,123,255,0.06))',
                boxShadow:
                  '0 18px 48px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08)',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                fontSize: '0.72rem',
                fontWeight: 700,
                color: '#dfe6ff',
                backdropFilter: 'blur(8px)',
                animation: 'badgeFloatIn 0.6s ease',
                '@keyframes badgeFloatIn': {
                  '0%': { opacity: 0, transform: 'translateY(-6px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#7cf3ff',
                  boxShadow: '0 0 0 6px rgba(124,243,255,0.20)',
                  animation: 'liveDotPulse 1.8s ease-in-out infinite',
                  '@keyframes liveDotPulse': {
                    '0%, 100%': { boxShadow: '0 0 0 6px rgba(124,243,255,0.20)' },
                    '50%': { boxShadow: '0 0 0 11px rgba(124,243,255,0)' },
                  },
                }}
              />
              {greeting}{user?.name || user?.username ? ` · ${user.name || user.username}` : ''}
            </Box>

            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.1rem', sm: '2.4rem', md: '3rem' },
                color: '#f5f7ff',
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                fontFamily: '"Space Grotesk", "Segoe UI", sans-serif',
                textShadow: '0 12px 36px rgba(0,0,0,0.4)',
                maxWidth: '880px',
              }}
            >
              Onde você quer{' '}
              <Box
                component="span"
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  background:
                    'linear-gradient(120deg, #7cf3ff 0%, #b794ff 45%, #ffd86b 100%)',
                  backgroundSize: '220% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'gradientShift 7s ease-in-out infinite',
                  '@keyframes gradientShift': {
                    '0%': { backgroundPosition: '0% center' },
                    '50%': { backgroundPosition: '100% center' },
                    '100%': { backgroundPosition: '0% center' },
                  },
                }}
              >
                começar hoje
              </Box>
              ?
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: '720px',
                color: 'rgba(233,238,255,0.78)',
                fontSize: { xs: '1rem', md: '1.08rem' },
                lineHeight: 1.6,
              }}
            >
              Sua central da suíte{' '}
              <Box
                component="span"
                sx={{ color: '#f5f7ff', fontWeight: 600 }}
              >
                {suiteName}
              </Box>
              . {isOwner
                ? 'Os produtos contratados ficam ativos, os demais você pode habilitar quando quiser.'
                : 'Aqui estão os produtos liberados para o seu usuário.'}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mt: { xs: 0.5, md: 0.75 },
                justifyContent: { xs: 'flex-start', md: 'center' },
              }}
            >
              {[
                {
                  label: `${accessibleCount} ${accessibleCount === 1 ? 'ativo' : 'ativos'}`,
                  dot: '#48fda3',
                  glow: 'rgba(72,253,163,0.25)',
                },
                ...(availableCount > 0 ? [{
                  label: `${availableCount} disponível${availableCount !== 1 ? 'is' : ''} para ativação`,
                  dot: '#ffd86b',
                  glow: 'rgba(255,216,107,0.25)',
                }] : []),
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.85,
                    px: 1.4,
                    py: 0.55,
                    borderRadius: '999px',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.10)',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: 'rgba(233,238,255,0.88)',
                    letterSpacing: '0.02em',
                    backdropFilter: 'blur(6px)',
                    transition: 'background 0.2s ease, border-color 0.2s ease',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.07)',
                      borderColor: 'rgba(255,255,255,0.18)',
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: item.dot,
                      boxShadow: `0 0 0 4px ${item.glow}`,
                    }}
                  />
                  {item.label}
                </Box>
              ))}
            </Box>

            {renderMyLinksButton({
              display: { xs: 'inline-flex', md: 'none' },
              alignSelf: 'flex-start',
              mt: 1,
            })}

            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                right: 0,
                bottom: { md: -4, lg: 0 },
                zIndex: 15,
              }}
            >
              {renderMyLinksButton()}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: { xs: 2.25, md: 'clamp(18px, 2.5vw, 26px)' },
            }}
          >
            {visibleProdutos.map((produto, index) => {
              const accessible = canAccessProductCard(produto);
              const isComingSoon = Boolean(produto.comingSoon);
              const canActivate = !produto.activationDisabled;
              const clickable = accessible && !isComingSoon;
              const tooltip = isComingSoon
                ? 'Em desenvolvimento'
                : accessible
                  ? ''
                  : canActivate
                    ? 'Você não tem acesso a esse produto'
                    : 'Produto indisponível para ativação no checkout';
              return (
                <Box
                  key={produto.productKey || index}
                  onClick={clickable ? () => navigate(getProductPath(produto)) : undefined}
                  title={tooltip}
                  sx={{
                    position: 'relative',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    cursor: clickable ? 'pointer' : 'not-allowed',
                    backgroundColor: produto.backgroundColor,
                    height: {
                      xs: 'clamp(200px, 32vw, 230px)',
                      md: 'clamp(220px, 24vh, 250px)',
                      lg: 'clamp(230px, 22vh, 260px)',
                    },
                    boxShadow: 'none',
                    transition: 'transform 0.4s ease, filter 0.4s ease',
                    transform: 'translateY(0)',
                    filter: clickable
                      ? 'none'
                      : isComingSoon
                        ? 'saturate(0.85)'
                        : 'grayscale(0.3) saturate(0.8)',
                    animation: `cardReveal 0.7s ease ${index * 0.08}s forwards`,
                    opacity: 0,
                    ...cardAnimation,
                    '&:hover': {
                      transform: clickable ? 'translateY(-6px) scale(1.01)' : 'translateY(0)',
                    },
                    '&:hover img': {
                      transform: clickable ? 'scale(1.12)' : 'scale(1)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      width: '100%',
                      p: 3,
                    }}
                  >
                    <img
                      src={produto.imagem}
                      alt={produto.nome}
                      style={{
                        maxHeight: produto.logoSize || '80px',
                        maxWidth: '78%',
                        objectFit: 'contain',
                        transition: 'transform 0.5s ease, filter 0.5s ease',
                      }}
                    />
                  </Box>

                  {isComingSoon ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'radial-gradient(ellipse at center, rgba(7,12,26,0.55) 0%, rgba(7,12,26,0.92) 80%)',
                        backdropFilter: 'blur(9px)',
                        WebkitBackdropFilter: 'blur(9px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.75,
                        px: 2.5,
                        textAlign: 'center',
                        overflow: 'hidden',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          background:
                            'linear-gradient(115deg, transparent 38%, rgba(255,255,255,0.07) 50%, transparent 62%)',
                          transform: 'translateX(-110%)',
                          animation: 'comingSoonShimmer 5s ease-in-out infinite',
                          pointerEvents: 'none',
                        },
                        '@keyframes comingSoonShimmer': {
                          '0%': { transform: 'translateX(-110%)' },
                          '70%, 100%': { transform: 'translateX(110%)' },
                        },
                        '@keyframes comingSoonPulse': {
                          '0%, 100%': { boxShadow: '0 0 0 4px rgba(255,216,107,0.25)' },
                          '50%': { boxShadow: '0 0 0 9px rgba(255,216,107,0)' },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          zIndex: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1.25,
                          px: 1.85,
                          py: 0.55,
                          mb: 0.75,
                          borderRadius: '999px',
                          background: 'rgba(255,216,107,0.14)',
                          border: '1px solid rgba(255,216,107,0.34)',
                          backdropFilter: 'blur(8px)',
                          color: '#ffd86b',
                          fontSize: '0.66rem',
                          fontWeight: 700,
                          letterSpacing: '0.22em',
                          textTransform: 'uppercase',
                          boxShadow: '0 10px 26px rgba(0,0,0,0.35)',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: '#ffd86b',
                            animation: 'comingSoonPulse 2.4s ease-in-out infinite',
                          }}
                        />
                        Em desenvolvimento
                      </Box>

                      <Typography
                        component="span"
                        sx={{
                          position: 'relative',
                          zIndex: 1,
                          fontFamily: '"Space Grotesk", "Segoe UI", sans-serif',
                          fontWeight: 700,
                          fontSize: { xs: '1.55rem', md: '1.7rem' },
                          letterSpacing: '-0.02em',
                          lineHeight: 1.05,
                          background:
                            'linear-gradient(120deg, #f5f7ff 0%, #ffd86b 65%, #ffb86b 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          textShadow: '0 6px 24px rgba(0,0,0,0.45)',
                        }}
                      >
                        Próximo lançamento
                      </Typography>

                      <Box
                        sx={{
                          position: 'relative',
                          zIndex: 1,
                          width: 36,
                          height: 2,
                          borderRadius: '999px',
                          background:
                            'linear-gradient(90deg, transparent, rgba(255,216,107,0.6), transparent)',
                          my: 0.6,
                        }}
                      />

                      <Typography
                        sx={{
                          position: 'relative',
                          zIndex: 1,
                          color: 'rgba(255,255,255,0.96)',
                          fontSize: { xs: '0.86rem', md: '0.9rem' },
                          fontWeight: 500,
                          maxWidth: '280px',
                          lineHeight: 1.55,
                          letterSpacing: '0.005em',
                          textShadow:
                            '0 2px 10px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.5)',
                        }}
                      >
                        {produto.comingSoonDescription}
                      </Typography>
                    </Box>
                  ) : !accessible && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.6) 100%)',
                        backdropFilter: 'blur(2px)',
                        WebkitBackdropFilter: 'blur(2px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                      }}
                    >
                      <Button
                        type="button"
                        variant="contained"
                        aria-label={`Ativar ${produto.nome}`}
                        disabled={!canActivate}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!canActivate) return;
                          setCheckoutProductKey(produto.productKey);
                        }}
                        sx={{
                          borderRadius: '999px',
                          textTransform: 'uppercase',
                          fontWeight: 800,
                          fontSize: '0.72rem',
                          letterSpacing: '0.18em',
                          px: 2.4,
                          py: 0.85,
                          color: '#dffbff',
                          background:
                            'linear-gradient(135deg, rgba(124,243,255,0.16), rgba(183,148,255,0.12))',
                          border: '1px solid rgba(124,243,255,0.34)',
                          backdropFilter: 'blur(8px)',
                          WebkitBackdropFilter: 'blur(8px)',
                          boxShadow:
                            '0 14px 34px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.12)',
                          '&:hover': {
                            background:
                              'linear-gradient(135deg, rgba(124,243,255,0.23), rgba(183,148,255,0.17))',
                            borderColor: 'rgba(124,243,255,0.52)',
                            boxShadow:
                              '0 18px 42px rgba(0,0,0,0.44), 0 0 0 5px rgba(124,243,255,0.08), inset 0 1px 0 rgba(255,255,255,0.16)',
                          },
                          '&.Mui-disabled': {
                            color: 'rgba(223,251,255,0.72)',
                            borderColor: 'rgba(148,163,184,0.30)',
                            background: 'rgba(148,163,184,0.14)',
                          },
                        }}
                      >
                        {canActivate ? 'Ativar' : 'Sem acesso'}
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })}
            {Array.from({ length: placeholdersCount }).map((_, idx) => (
              <Box
                key={`placeholder-${idx}`}
                sx={{
                  position: 'relative',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  background:
                    'linear-gradient(135deg, rgba(124,243,255,0.07) 0%, rgba(183,148,255,0.06) 55%, rgba(255,216,107,0.04) 100%)',
                  height: {
                    xs: 'clamp(200px, 32vw, 230px)',
                    md: 'clamp(220px, 24vh, 250px)',
                    lg: 'clamp(230px, 22vh, 260px)',
                  },
                  border: '1px solid rgba(255,255,255,0.09)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  transition: 'border-color 0.3s ease, background 0.3s ease',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(115deg, transparent 38%, rgba(255,255,255,0.04) 50%, transparent 62%)',
                    transform: 'translateX(-110%)',
                    animation: 'phShimmer 7s ease-in-out infinite',
                    pointerEvents: 'none',
                  },
                  '@keyframes phShimmer': {
                    '0%': { transform: 'translateX(-110%)' },
                    '60%, 100%': { transform: 'translateX(110%)' },
                  },
                  '@keyframes phFloat': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-5px)' },
                  },
                  '&:hover': {
                    borderColor: 'rgba(124,243,255,0.22)',
                    background:
                      'linear-gradient(135deg, rgba(124,243,255,0.11) 0%, rgba(183,148,255,0.09) 55%, rgba(255,216,107,0.06) 100%)',
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '12px',
                      background: 'rgba(124,243,255,0.08)',
                      border: '1px solid rgba(124,243,255,0.20)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(124,243,255,0.6)',
                      animation: 'phFloat 4s ease-in-out infinite',
                    }}
                  >
                    <Box
                      component="svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
                      <path d="M19 14l.95 2.3L22.25 17.25l-2.3.95L19 20.5l-.95-2.3L15.75 17.25l2.3-.95L19 14z" />
                    </Box>
                  </Box>

                  <Typography
                    variant="overline"
                    sx={{
                      letterSpacing: '0.24em',
                      color: 'rgba(124,243,255,0.55)',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      lineHeight: 1,
                    }}
                  >
                    Em breve
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: '"Space Grotesk", "Segoe UI", sans-serif',
                      fontWeight: 600,
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      letterSpacing: '-0.01em',
                      lineHeight: 1.2,
                      color: 'rgba(245,247,255,0.55)',
                    }}
                  >
                    Novo produto a caminho
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <ProductCheckoutModal
        open={Boolean(checkoutProductKey)}
        initialProductKey={checkoutProductKey}
        title="Ativar produtos"
        onClose={() => setCheckoutProductKey(null)}
      />
    </>
  );
};

export default Products;
